import { api, FetchRequest } from '../../../api/api';

/**
 * Diet ID's webhook latency is not documented, so these are a starting point to
 * revisit once measured against a real assessment.
 */
export const DIET_ID_POLL_INTERVAL_MS = 2000;
export const DIET_ID_POLL_ATTEMPTS = 15;

/** A result counts for this visit only if it finished this recently, since the form repeats per study. */
export const DIET_ID_RESULT_WINDOW_MS = 12 * 60 * 60 * 1000;

export function isRecentResult(
  completedAt: number | null,
  now: number = Date.now()
): completedAt is number {
  return completedAt !== null && completedAt >= now - DIET_ID_RESULT_WINDOW_MS;
}

export interface DietIdPollOptions {
  intervalMs?: number;
  attempts?: number;
  /** Injectable so tests do not wait in real time. */
  delay?: (ms: number) => Promise<void>;
  /** Returns false to abandon polling, e.g. once the screen has unmounted. */
  shouldContinue?: () => boolean;
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

// POST bodies are not read by these endpoints, so the participant goes in the query.
const withParticipant = (path: string, studyParticipantId?: number) =>
  studyParticipantId
    ? `${path}?studyParticipantId=${studyParticipantId}`
    : path;

/** api.get/post hand back a FetchRequest, not a parsed body, and resolve on any HTTP status. */
async function readJson<T>(request: FetchRequest): Promise<T> {
  const response = await request.response;
  if (!response.ok)
    throw new Error(`Diet ID request failed (${response.status})`);
  return (await response.json()) as T;
}

// Stored as the element's answer when Diet ID is off for the study, so a required element doesn't block the form.
export const DIET_ID_NOT_AVAILABLE = 'not-available';

/** True or false as the server answers; null when it couldn't be asked. */
export async function isDietIdEnabled(
  studyParticipantId?: number
): Promise<boolean | null> {
  try {
    const body = await readJson<{ enabled?: boolean }>(
      await api.get(
        withParticipant('diet-id/me/config', studyParticipantId),
        {}
      )
    );
    return body?.enabled === true;
  } catch {
    return null;
  }
}

function toMillis(value?: string | null): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/** Epoch millis of the participant's most recent assessment, or null. */
export async function getLatestCompletedAt(
  studyParticipantId?: number
): Promise<number | null> {
  const body = await readJson<{ completedAt?: string | null }>(
    await api.get(withParticipant('diet-id/me/status', studyParticipantId), {})
  );
  return toMillis(body?.completedAt);
}

/**
 * Asks the server to pull the result from Diet ID now. Throws when the pull
 * fails, so the caller can fall back to waiting for the webhook.
 */
export async function syncDietIdResult(
  studyParticipantId?: number
): Promise<number | null> {
  const response = await (
    await api.post(withParticipant('diet-id/me/sync', studyParticipantId), {})
  ).response;
  if (!response.ok) throw new Error(`Diet ID sync failed (${response.status})`);
  const body = (await response.json()) as { completedAt?: string | null };
  return toMillis(body?.completedAt);
}

/** Signed, single-use URL for the participant to open. */
export async function createDietIdSession(
  studyParticipantId?: number
): Promise<string> {
  const body = await readJson<{ url?: string }>(
    await api.post(
      withParticipant('diet-id/me/session', studyParticipantId),
      {}
    )
  );
  const url = body?.url;
  if (!url) throw new Error('Diet ID did not return a session URL');
  return url;
}

/** Waits for an assessment newer than `before`. Null means none arrived, which must not
 *  count as completion: it can't be told apart from closing the assessment early. */
export async function awaitDietIdResult(
  before: number | null,
  options: DietIdPollOptions = {},
  studyParticipantId?: number
): Promise<number | null> {
  const {
    intervalMs = DIET_ID_POLL_INTERVAL_MS,
    attempts = DIET_ID_POLL_ATTEMPTS,
    delay = sleep,
    shouldContinue = () => true
  } = options;

  for (let attempt = 0; attempt < attempts; attempt++) {
    await delay(intervalMs);
    if (!shouldContinue()) return null;
    let after: number | null = null;
    try {
      after = await getLatestCompletedAt(studyParticipantId);
    } catch {
      // A failed check means "not yet", not a reason to abandon the rest.
      continue;
    }
    if (after !== null && (before === null || after > before)) {
      return after;
    }
  }
  return null;
}
